/**
 * The deployment environments the app is built for. The value comes from the
 * `VITE_APP_ENVIRONMENT` build-time variable (see the per-environment `.env.*` files).
 */
export type AppEnvironment = 'Dev' | 'Test' | 'Prod';

/**
 * Case-insensitive check of the current `VITE_APP_ENVIRONMENT` against a target environment.
 *
 * Accepts any casing of the configured value (e.g. `DEV` | `Dev` | `dev` all match `'Dev'`,
 * and likewise for `Test` / `Prod`), so a mis-cased environment value still resolves correctly.
 *
 * This only changes how the environment string is *matched* — it does not change which
 * environment a feature is gated to. A Dev-only flag stays Dev-only; it simply no longer
 * breaks if the value is provided as `DEV` or `dev` instead of `Dev`.
 */
export function isAppEnvironment(target: AppEnvironment): boolean {
  const current = String(import.meta.env['VITE_APP_ENVIRONMENT'] ?? '')
    .trim()
    .toLowerCase();
  return current === target.toLowerCase();
}

/**
 * Whether the migration rollback feature is available in the running app.
 *
 * Rollback shipped Dev-only during its incubation; it is now generally
 * available (GA) across all environments. This single helper is the one place
 * that governs rollback visibility — components must call it rather than
 * re-deriving an environment check inline, so the feature can be re-gated
 * (e.g. temporarily disabled) from one location if ever needed.
 */
export function isRollbackEnabled(): boolean {
  return true;
}

/**
 * Whether recurring Org Sync scheduling (attaching a cron schedule to a migration, incl.
 * Division-to-Division) is available in the running app.
 *
 * Scheduling shipped Dev-only during its incubation; it is now generally available across all
 * environments. Enabled by default; set `VITE_ORG_SYNC_SCHEDULING=false` to re-gate it from one
 * place (build-time kill switch) without reintroducing an environment check inline.
 */
export function isOrgSyncSchedulingEnabled(): boolean {
  const flag = String(import.meta.env['VITE_ORG_SYNC_SCHEDULING'] ?? '')
    .trim()
    .toLowerCase();
  return flag !== 'false';
}

/**
 * Whether contextual failure messaging (distinguishing an import that failed during Terraform
 * planning — no changes applied — from one that failed during apply — changes may be partial) is
 * shown in the running app.
 *
 * This surfaces the backend `FailurePhase` to the user so a failed migration explains what happened.
 * Enabled by default across all environments; set `VITE_CONTEXTUAL_FAILURE_MESSAGING=false` to
 * re-gate it from one place.
 */
export function isContextualFailureMessagingEnabled(): boolean {
  const flag = String(import.meta.env['VITE_CONTEXTUAL_FAILURE_MESSAGING'] ?? '')
    .trim()
    .toLowerCase();
  return flag !== 'false';
}
